import { Device } from '@twilio/voice-sdk';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { TWILIO_CALL_EVENTS } from '@shared/constants';
import { useEffect } from 'react';
import { TwilioCall } from '@/types/app.types';
import { normalizeTwilioIncomingCall } from './helpers';
import { AppIncomingCall } from '../types';

export function useCallEvents(
  onAccept: (call: AppIncomingCall) => void,
  onDisconnect: (call: AppIncomingCall) => void
) {
  const { call: activeCall, device: activeDevice } = useDeviceStore();
  const call = (activeCall?.providerCall as TwilioCall | undefined) ?? null;
  const device = (activeDevice?.providerDevice as Device | undefined) ?? null;

  function handleAccept(twilioCall: TwilioCall) {
    onAccept(normalizeTwilioIncomingCall(twilioCall, device));
  }

  function handleDisconnect(twilioCall?: TwilioCall) {
    // 'reject'/'cancel' are emitted by the Twilio SDK with no payload — the call this listener
    // was bound to (the closure's `call`) is the authoritative identity in that case.
    const eventCall = twilioCall ?? call;
    if (!eventCall) return;

    // Stale-listener guard: this listener stays bound to whichever call was device.store.call
    // when this effect last ran; if the store's active call has already moved on (e.g. a
    // waiting call was promoted synchronously while this event was in flight), don't reprocess
    // it as "the" active call ending.
    const currentCall = (useDeviceStore.getState().call?.providerCall as TwilioCall | undefined) ?? null;
    if (eventCall !== currentCall) return;

    onDisconnect(normalizeTwilioIncomingCall(eventCall, device));
  }

  function handleCallEvents() {
    call?.on(TWILIO_CALL_EVENTS.accept, handleAccept);
    call?.on(TWILIO_CALL_EVENTS.disconnect, handleDisconnect);
    call?.on(TWILIO_CALL_EVENTS.reject, handleDisconnect);
    call?.on(TWILIO_CALL_EVENTS.cancel, handleDisconnect);

    return handleUnregisterCallEvents;
  }

  function handleUnregisterCallEvents() {
    call?.off(TWILIO_CALL_EVENTS.accept, handleAccept);
    call?.off(TWILIO_CALL_EVENTS.disconnect, handleDisconnect);
    call?.off(TWILIO_CALL_EVENTS.reject, handleDisconnect);
    call?.off(TWILIO_CALL_EVENTS.cancel, handleDisconnect);
  }

  useEffect(handleCallEvents, [call]);
}
